'use client';

import { useState } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  ArrowDown01Icon,
  ArrowRight01Icon,
  Download01Icon,
  Link02Icon,
  SquareLock01Icon,
  Mail01Icon,
  AttachmentIcon,
  SecurityCheckIcon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { Text } from '../../../components/styled/text';
import { Collapsible } from '../../../components/collapsible';
import { downloadBase64AsFile } from '../../../utils/download-content.utils';
import { formatFileSize } from '../../documents-reports/utils';
import {
  type EmailInboxAttachment,
  type EmailInboxMessage,
  type EmailInboxPayload,
} from './email-inbox-payload';
import { EmailMessageBody } from '../../mfa/EmailMessageBody';
import { EmailRawSourceViewer } from '../../mfa/EmailRawSourceViewer';

function EmailTagBadges({ message }: { message: EmailInboxMessage }) {
  const t = useTranslations('designSystem.artifacts.emailInbox');
  const tags = message.tags;
  const hasAttachments =
    Boolean(tags?.hasAttachments) || (message.attachments ?? []).length > 0;
  const badges: Array<{
    key: string;
    label: string;
    icon: typeof SecurityCheckIcon;
  }> = [];

  if (tags?.isVerification) {
    badges.push({
      key: 'verification',
      label: t('tagVerification'),
      icon: SecurityCheckIcon,
    });
  }

  if (tags?.hasMagicLinks) {
    badges.push({
      key: 'magic-link',
      label: t('tagMagicLink'),
      icon: Link02Icon,
    });
  }

  if (hasAttachments) {
    badges.push({
      key: 'attachment',
      label: t('tagAttachment'),
      icon: AttachmentIcon,
    });
  }

  if (badges.length === 0) {
    return null;
  }

  return (
    <HStack gap={1} alignItems="center" flexShrink={0}>
      {badges.map(({ key, label, icon: Icon }) => (
        <HStack
          key={key}
          gap={1}
          px={1.5}
          py={0.5}
          borderRadius="sm"
          bg="bg.default"
          borderWidth="1px"
          borderColor="border.neutral.muted"
          alignItems="center"
        >
          <Icon size={10} />
          <Text fontSize="2xs" color="fg.muted" whiteSpace="nowrap">
            {label}
          </Text>
        </HStack>
      ))}
    </HStack>
  );
}

export type EmailInboxCardProps = {
  payload: EmailInboxPayload;
  isPending?: boolean;
};

function formatReceivedAt(receivedAt: string): string {
  const parsed = new Date(receivedAt);

  if (Number.isNaN(parsed.getTime())) {
    return receivedAt;
  }

  return parsed.toLocaleString();
}

function EmailRow({ message }: { message: EmailInboxMessage }) {
  const t = useTranslations('designSystem.artifacts.emailInbox');
  const [isOpen, setIsOpen] = useState(false);
  const headerLabel = message.subject?.trim() || t('noSubject');
  const body = message.content?.trim() ?? '';
  const attachments = message.attachments ?? [];
  const hasLinks = (message.links ?? []).length > 0;
  const hasRawSource =
    Boolean(message.rawPlain?.trim()) || Boolean(message.rawHtml?.trim());
  const hasBody =
    body.length > 0 || attachments.length > 0 || hasLinks || hasRawSource;

  return (
    <Collapsible.Root
      open={isOpen}
      onOpenChange={({ open }: { open: boolean }) => setIsOpen(open)}
      lazyMount
      unmountOnExit
      width="100%"
    >
      <Box
        width="100%"
        borderTopWidth="1px"
        borderTopColor="border.neutral.muted"
      >
        <HStack
          width="100%"
          h="36px"
          px={3}
          gap={1.5}
          alignItems="center"
          justify="space-between"
          cursor={hasBody ? 'pointer' : 'default'}
          bg="transparent"
          role={hasBody ? 'button' : undefined}
          tabIndex={hasBody ? 0 : undefined}
          _hover={hasBody ? { bg: 'bg.hover' } : undefined}
          onClick={
            hasBody ? () => setIsOpen((previous) => !previous) : undefined
          }
        >
          <HStack gap={1.5} alignItems="center" minW={0}>
            <Box
              display="flex"
              alignItems="center"
              color="fg.muted"
              opacity={hasBody ? 1 : 0}
            >
              {isOpen ? (
                <ArrowDown01Icon size={10} />
              ) : (
                <ArrowRight01Icon size={10} />
              )}
            </Box>
            <Text
              fontSize="xs"
              fontWeight="medium"
              color="fg.default"
              truncate
              minW={0}
            >
              {headerLabel}
            </Text>
          </HStack>
          <HStack gap={1.5} alignItems="center" flexShrink={0}>
            <EmailTagBadges message={message} />
            <Text fontSize="xs" fontFamily="mono" color="fg.muted">
              {formatReceivedAt(message.receivedAt)}
            </Text>
          </HStack>
        </HStack>
        {hasBody && (
          <Collapsible.Content width="100%">
            <Box
              width="100%"
              bg="transparent"
              borderTopWidth="1px"
              borderColor="border.neutral.muted"
              px={3}
              py={2}
            >
              <VStack width="100%" alignItems="stretch" gap={2}>
                {message.from?.trim() && (
                  <HStack gap={1.5} alignItems="baseline">
                    <Text fontSize="xs" color="fg.muted" flexShrink={0}>
                      {t('from')}
                    </Text>
                    <Text fontSize="xs" color="fg.default">
                      {message.from}
                    </Text>
                  </HStack>
                )}
                {message.subject?.trim() && (
                  <HStack gap={1.5} alignItems="baseline">
                    <Text fontSize="xs" color="fg.muted" flexShrink={0}>
                      {t('subject')}
                    </Text>
                    <Text fontSize="xs" color="fg.default">
                      {message.subject}
                    </Text>
                  </HStack>
                )}
                {/* SECURITY: Use EmailMessageBody / EmailRawSourceViewer only — never render message HTML. */}
                <EmailMessageBody content={body} links={message.links} />
                <EmailRawSourceViewer
                  rawPlain={message.rawPlain}
                  rawHtml={message.rawHtml}
                />
                {attachments.length > 0 && (
                  <EmailAttachmentsList attachments={attachments} />
                )}
              </VStack>
            </Box>
          </Collapsible.Content>
        )}
      </Box>
    </Collapsible.Root>
  );
}

function EmailAttachmentsList({
  attachments,
}: {
  attachments: EmailInboxAttachment[];
}) {
  const t = useTranslations('designSystem.artifacts.emailInbox');
  const hasDownloadableAttachment = attachments.some((attachment) =>
    Boolean(attachment.contentBase64),
  );

  return (
    <VStack width="100%" alignItems="stretch" gap={1}>
      <HStack gap={1} alignItems="center">
        <Text fontSize="xs" color="fg.muted">
          {t('attachments')}
        </Text>
        {!hasDownloadableAttachment && (
          <HStack gap={0.5} alignItems="center" color="fg.subtle">
            <SquareLock01Icon size={9} />
            <Text fontSize="2xs" color="fg.subtle">
              {t('attachmentsMetadataHint')}
            </Text>
          </HStack>
        )}
      </HStack>
      <VStack width="100%" alignItems="stretch" gap={1}>
        {attachments.map((attachment) => (
          <HStack
            key={attachment.id}
            gap={1.5}
            px={2}
            py={1}
            borderRadius="sm"
            bg="transparent"
            borderWidth="1px"
            borderStyle="dashed"
            borderColor="border.neutral.muted"
            cursor="default"
            alignItems="center"
          >
            <AttachmentIcon size={12} color="var(--colors-fg-subtle)" />
            <Text fontSize="xs" color="fg.muted" maxW="180px" truncate>
              {attachment.name}
            </Text>
            <Text fontSize="2xs" color="fg.subtle">
              {formatFileSize(attachment.size)}
            </Text>
            {attachment.contentType && (
              <Text
                fontSize="2xs"
                fontFamily="mono"
                color="fg.subtle"
                truncate
                minW={0}
              >
                {attachment.contentType}
              </Text>
            )}
            {attachment.contentBase64 && (
              <button
                type="button"
                aria-label={t('downloadAttachment')}
                onClick={() =>
                  downloadBase64AsFile({
                    contentBase64: attachment.contentBase64 ?? '',
                    filename: attachment.name,
                    mimeType: attachment.contentType,
                  })
                }
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 2,
                  border: 0,
                  background: 'transparent',
                  cursor: 'pointer',
                }}
              >
                <Download01Icon size={12} />
              </button>
            )}
          </HStack>
        ))}
      </VStack>
    </VStack>
  );
}

export function EmailInboxCard({ payload }: EmailInboxCardProps) {
  const t = useTranslations('designSystem.artifacts.emailInbox');
  const hasAnyEmails = payload.messages.length > 0;

  return (
    <Box
      borderWidth="1px"
      borderColor="border.neutral.muted"
      borderRadius="md"
      bg="transparent"
      overflow="hidden"
      width="100%"
    >
      <HStack
        justify="space-between"
        alignItems="center"
        px={3}
        h="36px"
        bg="transparent"
      >
        <HStack gap={1.5} alignItems="center" minW={0}>
          <Box display="flex" alignItems="center" color="fg.muted">
            <Mail01Icon size={12} />
          </Box>
          <Text fontSize="xs" fontWeight="medium" color="fg.default">
            {t('title')}
          </Text>
        </HStack>
        {payload.emailAddress && (
          <Text
            fontSize="xs"
            fontFamily="mono"
            color="fg.muted"
            truncate
            minW={0}
          >
            {payload.emailAddress}
          </Text>
        )}
      </HStack>

      {!hasAnyEmails ? (
        <Box
          px={3}
          py={2}
          borderTopWidth="1px"
          borderTopColor="border.neutral.muted"
        >
          <Text fontSize="xs" color="fg.muted">
            {t('empty')}
          </Text>
        </Box>
      ) : (
        payload.messages.map((message) => (
          <EmailRow key={message.id} message={message} />
        ))
      )}
    </Box>
  );
}
